// CONTRACT:C1-MAP-CLIENT.1.0
// COP — non-modal toast notifications (snackbar stack).
//
// A tiny imperative API (`pushToast`) backed by a module-level store, so ANY
// module — React or imperative render-loop code — can raise a toast without a
// prop chain or context. The <Toasts/> host subscribes to the store and renders
// the stack (top-right, clear of the classification banner). Toasts are
// non-modal: they never trap focus or block the map. Auto-dismiss ~5s, manual
// dismiss via the close button. Severity color comes from the status-* tokens
// (the single APP-6 source) — "alert" = hostile red, "success" = neutral green,
// "info" = friendly blue.

import { useEffect, useState } from "react";
import "./ux.css";

/** Severity → border/title color (maps onto status-* tokens in ux.css). */
export type ToastTone = "info" | "alert" | "success";

/** Public shape accepted by `pushToast`. */
export interface ToastInput {
  /** Short, uppercase-styled headline. */
  title: string;
  /** Optional longer body line. */
  body?: string;
  /** Severity; defaults to "info". */
  tone?: ToastTone;
  /** Auto-dismiss delay in ms. 0 disables auto-dismiss. Defaults to 5000. */
  durationMs?: number;
}

/** A toast as held in the store (input + assigned identity). */
export interface Toast {
  id: number;
  title: string;
  body?: string;
  tone: ToastTone;
  durationMs: number;
}

const DEFAULT_DURATION_MS = 5000;

// ---- Module-level store (a minimal event emitter) -----------------------

let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<(toasts: Toast[]) => void>();

function emit(): void {
  // Hand out a fresh array so React sees a new reference and re-renders.
  const snapshot = toasts.slice();
  for (const listener of listeners) listener(snapshot);
}

function subscribe(listener: (toasts: Toast[]) => void): () => void {
  listeners.add(listener);
  listener(toasts.slice());
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Raise a toast from anywhere (React or imperative code). Returns the new
 * toast's id, which can be passed to `dismissToast` for early removal.
 */
export function pushToast(input: ToastInput): number {
  const toast: Toast = {
    id: nextId++,
    title: input.title,
    body: input.body,
    tone: input.tone ?? "info",
    durationMs: input.durationMs ?? DEFAULT_DURATION_MS,
  };
  toasts = [...toasts, toast];
  emit();
  return toast.id;
}

/** Remove a toast by id (no-op if already gone). */
export function dismissToast(id: number): void {
  const next = toasts.filter((t) => t.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  emit();
}

/** Remove every toast. */
export function clearToasts(): void {
  if (toasts.length === 0) return;
  toasts = [];
  emit();
}

// ---- Host component -----------------------------------------------------

/** Renders the live toast stack. Mount exactly one <Toasts/> in the shell. */
export function Toasts(): JSX.Element {
  const [items, setItems] = useState<Toast[]>(() => toasts.slice());

  useEffect(() => subscribe(setItems), []);

  return (
    <div className="toasts" role="region" aria-label="Notifications" aria-live="polite">
      {items.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}

function ToastItem({ toast }: { toast: Toast }): JSX.Element {
  const [leaving, setLeaving] = useState(false);

  // Auto-dismiss after `durationMs` (0 disables it).
  useEffect(() => {
    if (toast.durationMs <= 0) return undefined;
    const id = window.setTimeout(() => dismissToast(toast.id), toast.durationMs);
    return () => window.clearTimeout(id);
  }, [toast.id, toast.durationMs]);

  const close = (): void => {
    // Honour reduced-motion: skip the exit animation and remove immediately.
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      dismissToast(toast.id);
      return;
    }
    setLeaving(true);
    window.setTimeout(() => dismissToast(toast.id), 140);
  };

  return (
    <div
      className={`toast toast--${toast.tone}`}
      role={toast.tone === "alert" ? "alert" : "status"}
      data-leaving={leaving || undefined}
    >
      <div className="toast__body">
        <div className="toast__title">{toast.title}</div>
        {toast.body ? <div className="toast__text">{toast.body}</div> : null}
      </div>
      <button
        type="button"
        className="toast__close"
        aria-label="Dismiss notification"
        onClick={close}
      >
        ×
      </button>
    </div>
  );
}
